import { AgreeToTermsPolicy, experience, InteractionEvent } from '@logto/schemas';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { registerUserWithoutSubmit } from '@/apis/experience';

import useApi from './use-api';
import useErrorHandler from './use-error-handler';
import useSubmitInteractionErrorHandler from './use-submit-interaction-error-handler';
import useTerms from './use-terms';

const useSocialRegister = (connectorId: string, replace?: boolean) => {
  const handleError = useErrorHandler();
  const asyncRegisterWithSocial = useApi(registerUserWithoutSubmit);
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigate();

  const preRegisterErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.Register, {
    linkSocial: connectorId,
    replace,
  });

  return useCallback(
    async (verificationId: string) => {
      /**
       * Agree to terms and conditions first before proceeding
       * If the agreement policy is `Manual`, the user must agree to the terms to reach this step.
       * Therefore, skip the check for `Manual` policy.
       */
      if (agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && !(await termsValidation())) {
        navigate('/' + experience.routes.signIn);
        return;
      }

      const [error] = await asyncRegisterWithSocial(verificationId, 'social');

      if (error) {
        await handleError(error, preRegisterErrorHandler);

        return;
      }

      // Social identity staged in the interaction (user not created yet).
      // Always collect the extra profile BEFORE creating the user: connectors that
      // supply a verified email (Google/Apple) would otherwise satisfy the sign-up
      // requirements and complete the registration instantly, skipping both data
      // collection and the MoEngage sync. The extra-profile submit creates the user;
      // if email/phone is still missing (e.g. LINE) the error handler routes there,
      // and the post-verification step submits directly so the form never shows twice.
      navigate('/continue/extra-profile', {
        replace: true,
        state: {
          interactionEvent: InteractionEvent.Register,
          isSocialLogin: true,
          socialConnectorId: connectorId,
        },
      });
    },
    [
      agreeToTermsPolicy,
      asyncRegisterWithSocial,
      connectorId,
      handleError,
      navigate,
      preRegisterErrorHandler,
      termsValidation,
    ]
  );
};

export default useSocialRegister;
